import type { FilterConfig } from '@/types/filters'

/**
 * LOTE 3: Configurações de filtros para dados profissionais e responsáveis
 * 
 * DADOS PROFISSIONAIS E RESPONSÁVEIS (8 campos):
 * - Gestor Responsável
 * - Responsável no Cliente  
 * - Telefone
 * - Email Pessoal
 * - Nome da Mãe
 * - Nome do Pai
 * - Nome do Cônjuge
 * - Gênero
 */

export const LOTE_3_FILTER_CONFIGS: FilterConfig[] = [
  // ===== DADOS PROFISSIONAIS =====
  {
    columnId: 'responsibleName',
    label: 'Gestor Responsável',
    type: 'select',
    operators: ['equals', 'in', 'notIn', 'contains'],
    options: [], // Será populado dinamicamente
    placeholder: 'Selecione o gestor...',
    category: 'Dados Profissionais'
  },
  {
    columnId: 'clientResponsible',
    label: 'Responsável no Cliente',
    type: 'text',
    operators: ['contains', 'equals', 'startsWith', 'endsWith'],
    placeholder: 'Digite o nome do responsável no cliente...',
    category: 'Dados Profissionais'
  },
  
  // ===== DADOS DE CONTATO =====
  {
    columnId: 'phone',
    label: 'Telefone',
    type: 'text',
    operators: ['contains', 'equals', 'startsWith'],
    placeholder: 'Digite o telefone...',
    category: 'Dados de Contato'
  },
  {
    columnId: 'personalEmail',
    label: 'Email Pessoal',
    type: 'text',
    operators: ['contains', 'equals', 'startsWith', 'endsWith'],
    placeholder: 'Digite o email pessoal...',
    category: 'Dados de Contato'
  },
  
  // ===== DADOS FAMILIARES =====
  {
    columnId: 'motherName',
    label: 'Nome da Mãe',
    type: 'text',
    operators: ['contains', 'equals', 'startsWith'],
    placeholder: 'Digite o nome da mãe...',
    category: 'Dados Pessoais'
  },
  {
    columnId: 'fatherName',
    label: 'Nome do Pai',
    type: 'text',
    operators: ['contains', 'equals', 'startsWith'],
    placeholder: 'Digite o nome do pai...',
    category: 'Dados Pessoais'
  },
  {
    columnId: 'spouseName',
    label: 'Nome do Cônjuge',
    type: 'text',
    operators: ['contains', 'equals', 'startsWith', 'isEmpty', 'isNotEmpty'],
    placeholder: 'Digite o nome do cônjuge...',
    category: 'Dados Pessoais'
  },
  
  // ===== DADOS ADICIONAIS =====
  {
    columnId: 'gender',
    label: 'Gênero',
    type: 'select',
    operators: ['equals', 'in', 'notIn'],
    options: [
      { value: 'male', label: 'Masculino' },
      { value: 'female', label: 'Feminino' },
      { value: 'non_binary', label: 'Não-binário' },
      { value: 'transgender_male', label: 'Transgênero Masculino' },
      { value: 'transgender_female', label: 'Transgênero Feminino' },
      { value: 'other', label: 'Outro' },
      { value: 'prefer_not_to_say', label: 'Prefiro não informar' }
    ],
    placeholder: 'Selecione o gênero...',
    category: 'Dados Pessoais'
  }
]

/**
 * Acessores para campos do Lote 3
 * Campos que precisam de transformação ou acesso especial
 */
export const LOTE_3_FIELD_ACCESSORS = {
  // Campos diretos
  responsibleName: (item: any) => item.responsibleName,
  clientResponsible: (item: any) => item.clientResponsible,
  phone: (item: any) => item.phone,
  personalEmail: (item: any) => item.personalEmail,
  motherName: (item: any) => item.motherName,
  fatherName: (item: any) => item.fatherName,
  spouseName: (item: any) => item.spouseName,
  gender: (item: any) => item.gender,
}

/**
 * Presets prontos para Lote 3
 */
export const LOTE_3_PRESETS = [
  {
    id: 'colaboradores_por_gestor',
    name: 'Colaboradores por Gestor Específico',
    description: 'Filtrar colaboradores de um gestor específico',
    filters: [
      {
        columnId: 'activityStatus',
        operator: 'equals' as const,
        value: 'active',
        type: 'select' as const,
        label: 'Status de Atividade',
        enabled: true
      }
    ],
    isSystem: true,
    requiresInput: ['responsibleName'] // Indica que precisa de input do usuário
  },
  {
    id: 'colaboradores_com_telefone',
    name: 'Colaboradores com Telefone Cadastrado',
    description: 'Colaboradores que possuem telefone registrado',
    filters: [
      {
        columnId: 'activityStatus',
        operator: 'equals' as const,
        value: 'active',
        type: 'select' as const,
        label: 'Status de Atividade',
        enabled: true
      },
      {
        columnId: 'phone',
        operator: 'isNotEmpty' as const,
        value: null,
        type: 'text' as const,
        label: 'Telefone',
        enabled: true
      }
    ],
    isSystem: true
  },
  {
    id: 'colaboradores_casados',
    name: 'Colaboradores Casados/União Estável',
    description: 'Colaboradores com cônjuge registrado',
    filters: [
      {
        columnId: 'activityStatus',
        operator: 'equals' as const,
        value: 'active',
        type: 'select' as const,
        label: 'Status de Atividade',
        enabled: true
      },
      {
        columnId: 'maritalStatus',
        operator: 'in' as const,
        value: ['married', 'stable_union'],
        type: 'multiselect' as const,
        label: 'Estado Civil',
        enabled: true
      },
      {
        columnId: 'spouseName',
        operator: 'isNotEmpty' as const,
        value: null,
        type: 'text' as const,
        label: 'Nome do Cônjuge',
        enabled: true
      }
    ],
    isSystem: true
  }
] as const